import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Plus, Pencil, Trash2, FileText, Search } from "lucide-react";
import { toast } from "sonner";

const TIPOS_CONTRATO = [
  { v: "locacao", l: "Locação" },
  { v: "venda", l: "Venda" },
  { v: "servico", l: "Serviço" },
  { v: "outros", l: "Outros" },
];

const TIPO_LABEL = TIPOS_CONTRATO.reduce((a, t) => ({ ...a, [t.v]: t.l }), {});
const TIPO_COLOR = {
  locacao: "bg-blue-100 text-blue-700 border-blue-200",
  venda: "bg-emerald-100 text-emerald-700 border-emerald-200",
  servico: "bg-amber-100 text-amber-700 border-amber-200",
  outros: "bg-slate-100 text-slate-700 border-slate-200",
};

const EMPTY = {
  nome: "",
  tipo_equipamento: "",
  tipo_contrato: "locacao",
  descricao_solucao: "",
  condicoes_padrao: "",
  observacoes_padrao: "",
  equipamentos_vinculados: [],
  ativo: true,
};

export default function TemplatesProposta() {
  const [items, setItems] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [q, setQ] = useState("");
  const [filtroTipo, setFiltroTipo] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: tpls }, { data: cats }] = await Promise.all([
        api.get("/proposta/templates"),
        api.get("/proposta/catalogo/categorias"),
      ]);
      setItems(tpls || []);
      setCategorias(cats || []);
    } catch (e) {
      toast.error("Falha ao carregar templates");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const abrirNovo = () => {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  };

  const abrirEdicao = (t) => {
    setEditing(t);
    setForm({
      nome: t.nome || "",
      tipo_equipamento: t.tipo_equipamento || "",
      tipo_contrato: t.tipo_contrato || "locacao",
      descricao_solucao: t.descricao_solucao || "",
      condicoes_padrao: t.condicoes_padrao || "",
      observacoes_padrao: t.observacoes_padrao || "",
      equipamentos_vinculados: t.equipamentos_vinculados || [],
      ativo: t.ativo !== false,
    });
    setOpen(true);
  };

  const salvar = async () => {
    if (!form.nome.trim()) {
      toast.error("Nome obrigatório");
      return;
    }
    if (!form.descricao_solucao.trim()) {
      toast.error("Descrição da solução obrigatória");
      return;
    }
    try {
      if (editing) {
        await api.patch(`/proposta/templates/${editing.id}`, form);
        toast.success("Template atualizado");
      } else {
        await api.post("/proposta/templates", form);
        toast.success("Template criado");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao salvar");
    }
  };

  const remover = async (t) => {
    if (!window.confirm(`Remover o template "${t.nome}"?`)) return;
    try {
      await api.delete(`/proposta/templates/${t.id}`);
      toast.success("Template removido");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao remover");
    }
  };

  const filtrados = items.filter((t) => {
    const okQ = !q || t.nome.toLowerCase().includes(q.toLowerCase()) ||
                (t.descricao_solucao || "").toLowerCase().includes(q.toLowerCase());
    const okT = !filtroTipo || t.tipo_contrato === filtroTipo;
    return okQ && okT;
  });

  return (
    <div className="space-y-5" data-testid="templates-proposta-page">
      <div className="flex items-end justify-between">
        <div className="flex items-center gap-3">
          <FileText size={28} style={{ color: "#1B3A6B" }} />
          <div>
            <h1 className="text-3xl font-display font-bold" style={{ color: "#1B3A6B" }}>
              Templates de Proposta
            </h1>
            <p className="text-sm text-muted-foreground">
              Modelos pré-configurados de proposta comercial ({items.length})
            </p>
          </div>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={abrirNovo} data-testid="novo-template-btn" className="text-white" style={{ background: "#F97316" }}>
              <Plus size={14} className="mr-2" /> Novo template
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editing ? "Editar" : "Novo"} template de proposta</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Nome do template *</Label>
                <Input
                  data-testid="tpl-nome"
                  value={form.nome}
                  onChange={(e) => setForm({ ...form, nome: e.target.value })}
                  placeholder="Ex: Locação Padrão — Empilhadeira"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Tipo de contrato *</Label>
                  <select
                    data-testid="tpl-tipo-contrato"
                    className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                    value={form.tipo_contrato}
                    onChange={(e) => setForm({ ...form, tipo_contrato: e.target.value })}
                  >
                    {TIPOS_CONTRATO.map((t) => (
                      <option key={t.v} value={t.v}>{t.l}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Tipo de equipamento (categoria)</Label>
                  <Input
                    data-testid="tpl-tipo-equipamento"
                    list="tpl-categorias"
                    value={form.tipo_equipamento}
                    onChange={(e) => setForm({ ...form, tipo_equipamento: e.target.value })}
                    placeholder="Ex: Empilhadeira"
                  />
                  <datalist id="tpl-categorias">
                    {categorias.map((c) => <option key={c} value={c} />)}
                  </datalist>
                </div>
              </div>
              <div>
                <Label>Descrição da solução *</Label>
                <Textarea
                  data-testid="tpl-descricao"
                  rows={5}
                  value={form.descricao_solucao}
                  onChange={(e) => setForm({ ...form, descricao_solucao: e.target.value })}
                  placeholder="Texto comercial que aparecerá no corpo da proposta..."
                />
              </div>
              <div>
                <Label>Condições padrão</Label>
                <Textarea
                  data-testid="tpl-condicoes"
                  rows={2}
                  value={form.condicoes_padrao}
                  onChange={(e) => setForm({ ...form, condicoes_padrao: e.target.value })}
                  placeholder="Forma de pagamento, frete, prazo de entrega..."
                />
              </div>
              <div>
                <Label>Observações padrão</Label>
                <Textarea
                  data-testid="tpl-observacoes"
                  rows={2}
                  value={form.observacoes_padrao}
                  onChange={(e) => setForm({ ...form, observacoes_padrao: e.target.value })}
                  placeholder="Notas adicionais que sempre vão na proposta..."
                />
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  data-testid="tpl-ativo"
                  checked={form.ativo}
                  onCheckedChange={(v) => setForm({ ...form, ativo: v })}
                />
                <Label>Ativo (disponível para vendedores)</Label>
              </div>
              <Button
                data-testid="tpl-save"
                onClick={salvar}
                className="w-full text-white mt-2"
                style={{ background: "#1B3A6B" }}
              >
                {editing ? "Salvar alterações" : "Criar template"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="p-4">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex-1 relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="tpl-search"
              className="pl-9"
              placeholder="Buscar por nome ou descrição..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <select
            data-testid="tpl-filtro-tipo"
            className="border rounded-md px-3 py-2 text-sm"
            value={filtroTipo}
            onChange={(e) => setFiltroTipo(e.target.value)}
          >
            <option value="">Todos tipos</option>
            {TIPOS_CONTRATO.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
          </select>
        </div>
      </Card>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead>Equipamento</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Carregando...</TableCell></TableRow>
            ) : filtrados.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Nenhum template encontrado.</TableCell></TableRow>
            ) : filtrados.map((t) => (
              <TableRow key={t.id} data-testid={`tpl-row-${t.id}`}>
                <TableCell>
                  <div className="font-medium">{t.nome}</div>
                  {t.descricao_solucao && (
                    <div className="text-xs text-muted-foreground line-clamp-2 mt-0.5 max-w-md">
                      {t.descricao_solucao}
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  <span className={`inline-flex px-2 py-0.5 rounded text-xs border ${TIPO_COLOR[t.tipo_contrato] || ""}`}>
                    {TIPO_LABEL[t.tipo_contrato] || t.tipo_contrato}
                  </span>
                </TableCell>
                <TableCell>
                  {t.tipo_equipamento ? <Badge variant="outline">{t.tipo_equipamento}</Badge> : <span className="text-muted-foreground">—</span>}
                </TableCell>
                <TableCell>
                  <Badge variant={t.ativo ? "default" : "outline"}>
                    {t.ativo ? "Ativo" : "Inativo"}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="ghost" data-testid={`tpl-edit-${t.id}`} onClick={() => abrirEdicao(t)}>
                    <Pencil size={14} />
                  </Button>
                  <Button size="sm" variant="ghost" data-testid={`tpl-delete-${t.id}`} onClick={() => remover(t)}>
                    <Trash2 size={14} />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
